import type { LanguageType } from '@/types/language';

/**
 * ### AboutHeroCopy
 *
 * The company statement the About page opens on. The English headline is the
 * brand line and is the same in both languages; everything under it is
 * written per language.
 */
export type AboutHeroCopy = {
  /** The sentence that turns the brand line into a claim about the work */
  lead: string;

  /** What kind of company that makes Deeply, in one sentence */
  description: string;
};

/**
 * ### AboutSolutionCard
 *
 * One of the two products, as the About page states it: a line on what it is
 * for, the one figure that stands for it, and where to read more.
 */
export type AboutSolutionCard = {
  /** Stable identifier, used as the React key and the analytics item id */
  id: 'industrial' | 'safety';

  /** Product name, English in both languages */
  name: string;

  /** What the product is for, in one line */
  summary: string;

  /** The single figure that stands for the product */
  figure: string;

  /** What that figure is, and the range it was measured over */
  figureCaption: string;

  /** Where the card leads */
  href: string;
};

/**
 * ### AboutStrengthPillar
 *
 * One of the three answers to "why Deeply", each a claim about the company
 * rather than about a product.
 */
export type AboutStrengthPillar = {
  /** Stable identifier, used as the React key and to pick the icon */
  id: 'hearing' | 'hard-problem' | 'groundwork';

  /** The claim, as a short heading */
  title: string;

  /**
   * The one sentence that carries the claim, set large. A reader who takes
   * only this away from the pillar has the point.
   */
  lead: string;

  /** What backs the lead, read after it */
  points: ReadonlyArray<string>;
};

/**
 * ### AboutMilestone
 *
 * One line of the deployment record: when, where, and what was built. The
 * customer is named only where the name may be published; the automaker is
 * carried as Company H, the same way `llms.txt` carries it.
 */
export type AboutMilestone = {
  /** `YYYY.MM`, used as the React key and shown as written */
  period: string;

  /** Customer or site */
  customer: string;

  /** What was delivered */
  work: string;
};

/**
 * ### ABOUT_HERO_BY_LOCALE
 *
 * @see {@link AboutHeroCopy}
 */
export const ABOUT_HERO_BY_LOCALE: Record<LanguageType, AboutHeroCopy> = {
  ko: {
    lead: '소리를 데이터로, 데이터를 판단으로. 디플리는 소리 AI로 산업 현장의 눈과 귀가 됩니다',
    description:
      '디플리는 음향 신호처리와 딥러닝 기술을 바탕으로, 사람이 청각으로 판단하던 일을 소리 AI로 자동화하는 딥테크 기업입니다',
  },
  en: {
    lead: 'Sound into data, data into judgment. Deeply is the eyes and ears of the production floor',
    description:
      'Deeply is a deep-tech company that automates with sound AI the judgments people used to make by ear, built on acoustic signal processing and deep learning',
  },
};

/**
 * ### ABOUT_SOLUTION_CARDS_BY_LOCALE
 *
 * The two products in the order the site leads with them. Every figure here
 * is one the site already publishes elsewhere: the Industrial accuracy comes
 * from the press kit, the Safety F1-score from the TTA certification
 * materials. They are measured over different ranges and are never read as
 * one claim, which is why each carries its own caption.
 */
export const ABOUT_SOLUTION_CARDS_BY_LOCALE: Record<
  LanguageType,
  ReadonlyArray<AboutSolutionCard>
> = {
  ko: [
    {
      id: 'industrial',
      name: 'Listen AI Industrial',
      summary: '소리로 완성하는 무인 품질 검사와 설비 예지보전',
      figure: '99.98%',
      figureCaption: '누적 1억 회 분석 기준 정확도',
      href: '/solution/industrial',
    },
    {
      id: 'safety',
      name: 'Listen AI Safety',
      summary: '보이지 않는 위험을 소리로 가장 먼저 파악',
      figure: '98%+',
      figureCaption: 'TTA 인증 F1-score',
      href: '/solution/safety',
    },
  ],
  en: [
    {
      id: 'industrial',
      name: 'Listen AI Industrial',
      summary:
        'Unmanned quality inspection and predictive maintenance, done by sound',
      figure: '99.98%',
      figureCaption: 'Accuracy across 100 million analyses (press kit)',
      href: '/solution/industrial',
    },
    {
      id: 'safety',
      name: 'Listen AI Safety',
      summary: 'The first to know an unseen danger, by the sound it makes',
      figure: '98%+',
      figureCaption: 'TTA-certified F1-score',
      href: '/solution/safety',
    },
  ],
};

/**
 * ### AboutWhySound
 *
 * The question the technical case has to answer before any of it lands:
 * why sound in the first place. Held as its own copy rather than folded
 * into a pillar, because it is the premise the three pillars rest on.
 */
export type AboutWhySound = {
  /** The question, asked plainly */
  question: string;

  /** The answer, in one paragraph */
  answer: string;
};

/**
 * ### ABOUT_WHY_SOUND_BY_LOCALE
 *
 * @see {@link AboutWhySound}
 */
export const ABOUT_WHY_SOUND_BY_LOCALE: Record<LanguageType, AboutWhySound> = {
  ko: {
    question: '왜 소리인가',
    answer:
      '소리에는 글로 옮겨 적을 수 없는 정보가 들어 있습니다. 어떤 설비에서, 어떤 조건에서, 어떤 문제가 생겼을 때 나는 소리인지. 그 정보를 읽어내는 것이 디플리가 하는 일입니다',
  },
  en: {
    question: 'Why sound',
    answer:
      'Sound carries what cannot be written down: which machine, under what conditions, and what had gone wrong. Reading that is the work',
  },
};

/**
 * ### ABOUT_STRENGTH_TITLE_BY_LOCALE
 *
 * The heading of the Technical Strength section and the paragraph under it.
 */
export const ABOUT_STRENGTH_TITLE_BY_LOCALE: Record<
  LanguageType,
  { title: string; description: string }
> = {
  ko: {
    title: 'Since 2017, 오직 비언어 소리(Nonverbal Sound)에 집중했습니다',
    description:
      '음성인식(STT)이 아닙니다. 사람의 말이 아닌 소리, 기계음과 비명과 파열음과 이상음 그 자체를 이해하는 Pure Audio AI를 만듭니다',
  },
  en: {
    title: 'Since 2017, focused on nonverbal sound alone',
    description:
      'Not speech recognition. Pure audio AI that understands the sounds which are not speech, machine noise and screams and impacts and anomalies, as they are',
  },
};

/**
 * ### ABOUT_STRENGTH_PILLARS_BY_LOCALE
 *
 * @see {@link AboutStrengthPillar}
 */
export const ABOUT_STRENGTH_PILLARS_BY_LOCALE: Record<
  LanguageType,
  ReadonlyArray<AboutStrengthPillar>
> = {
  ko: [
    {
      id: 'hearing',
      title: '사람보다 더 잘 듣는 AI',
      lead: '소리는 복잡합니다',
      points: [
        '패턴과 주파수, 물리 신호가 뒤섞여 있어 영상보다 다루기 어렵습니다',
        '음성을 텍스트로 바꾸는 것이 아니라, 소리가 지닌 의미에 집중합니다',
        '수천 번의 신호처리 실험과 현장 실증을 거쳤고, h-index 기준 관련 분야 상위 랭킹 연구진을 두고 있습니다',
      ],
    },
    {
      id: 'hard-problem',
      title: '소리 AI만의 난제를 넘다',
      lead: '비전 AI와 달리 소리 AI에는 표준 모델이 없습니다',
      points: [
        '노이즈, 에코, 실시간성과 정확도를 모두 동시에 잡아야 합니다',
        '10년간의 R&D로 이 난제를 극복했습니다',
        '디플리의 논문은 세계 최고 권위의 신호처리 학회 ICASSP에 채택되었습니다',
      ],
    },
    {
      id: 'groundwork',
      title: 'LLM은 자본으로, 소리 AI는 근성으로',
      lead: '현장을 위해, 현장에 의해 만들어지는 소리 AI입니다',
      points: [
        'LLM은 GPU 등 자본 집약적 경쟁이지만, 소리 AI는 폐쇄적(Closed) 데이터 확보가 핵심입니다',
        '각 소리가 어떤 설비에서, 어떤 조건에서, 어떤 문제가 발생했을 때 나는지 알아야 합니다',
        '많이 듣는 것이 아니라 희소한 소리를 모아 학습했습니다. 규모가 아니라 현장 경험의 축적이 곧 기술력입니다',
      ],
    },
  ],
  en: [
    {
      id: 'hearing',
      title: 'An AI that hears better than we do',
      lead: 'Sound is complicated',
      points: [
        'Pattern, frequency and physical signal are mixed together, which makes it harder to work with than vision',
        'Not speech turned into text, but the meaning the sound itself carries',
        'Thousands of signal-processing experiments and field trials, with researchers ranked among the field leaders by h-index',
      ],
    },
    {
      id: 'hard-problem',
      title: 'Past the problems only sound has',
      lead: 'Unlike vision AI, sound AI has no standard model',
      points: [
        'Noise, echo, real-time response and accuracy all have to be solved at once',
        'A decade of R&D got past that',
        "Deeply's papers are accepted at ICASSP, the foremost signal-processing conference",
      ],
    },
    {
      id: 'groundwork',
      title: 'LLMs run on capital, sound AI on groundwork',
      lead: 'Sound AI is built for the floor, and by it',
      points: [
        'Where LLMs compete on GPU capital, sound AI turns on securing closed data',
        'It has to know which machine made the sound, under what conditions, and what had gone wrong',
        'Not hearing more, but gathering the rare sounds. Accumulated time on the floor is the capability',
      ],
    },
  ],
};

/**
 * ### ABOUT_MILESTONES_BY_LOCALE
 *
 * The deployment record, newest first. The automaker stays Company H here
 * for the same reason `llms.txt` keeps it that way: the name has not been
 * cleared for publication, and one page naming it would undo the rule
 * everywhere else.
 */
export const ABOUT_MILESTONES_BY_LOCALE: Record<
  LanguageType,
  ReadonlyArray<AboutMilestone>
> = {
  ko: [
    {
      period: '2026.02',
      customer: '지자체 10곳+',
      work: '안전 모니터링 솔루션',
    },
    {
      period: '2025.09',
      customer: '내장산국립공원',
      work: '안전 모니터링 솔루션',
    },
    { period: '2025.05', customer: '효성전기', work: '모터 구동 이상 분석' },
    {
      period: '2025.03',
      customer: 'H사',
      work: '체결 이상음 감지·분석 솔루션',
    },
    {
      period: '2024.09',
      customer: '코레일',
      work: '고속철도 기계 이상 감지 솔루션',
    },
    { period: '2024.06', customer: '인천교통공사', work: '화학물질 위험 감지' },
    {
      period: '2024.04',
      customer: '세종시청사 복합시설',
      work: '문화·체육시설 위험 감지',
    },
    {
      period: '2023.07',
      customer: 'DL이앤씨 / 롯데건설',
      work: '건설현장 소음·위험 모니터링',
    },
    {
      period: '2023.05',
      customer: '인천스타트업파크',
      work: '위험 감지 모니터링 서비스',
    },
    {
      period: '2023.04',
      customer: '강원랜드',
      work: '하이원리조트 위험 모니터링 솔루션',
    },
    {
      period: '2022.07',
      customer: '대한민국 육군 본부',
      work: '드론 엔진 이상음 감지',
    },
    {
      period: '2022.05',
      customer: 'KB, ITP',
      work: '부정거래 방지·도난 감지 사운드 프로젝트',
    },
    {
      period: '2022.03',
      customer: 'KT',
      work: '고령자 돌봄 솔루션, IoT 기반 커뮤니케이션 프로젝트',
    },
    { period: '2021.10', customer: 'LG CNS', work: '스피커 감정 감지 솔루션' },
    { period: '2021.10', customer: 'LG CNS', work: '국방 협업 기술 개발' },
    {
      period: '2020.09',
      customer: 'Amazon',
      work: '재난 대응 AWS 협업 프로젝트',
    },
    {
      period: '2019.06',
      customer: 'Google',
      work: 'Google for Startups 공동 솔루션 개발',
    },
    {
      period: '2019.05',
      customer: 'LG CNS',
      work: 'AI 기반 사운드 개발 프로젝트',
    },
    {
      period: '2018.09',
      customer: 'Meta',
      work: 'Asia Startup Program (with Asia Foundation)',
    },
  ],
  en: [
    {
      period: '2026.02',
      customer: '10+ local governments',
      work: 'Safety monitoring solution',
    },
    {
      period: '2025.09',
      customer: 'Naejangsan National Park',
      work: 'Safety monitoring solution',
    },
    {
      period: '2025.05',
      customer: 'Hyosung Electric',
      work: 'Motor drive anomaly analysis',
    },
    {
      period: '2025.03',
      customer: 'Company H',
      work: 'Fastening anomaly sound detection and analysis',
    },
    {
      period: '2024.09',
      customer: 'KORAIL',
      work: 'High-speed rail machinery anomaly detection',
    },
    {
      period: '2024.06',
      customer: 'Incheon Transit Corporation',
      work: 'Chemical hazard detection',
    },
    {
      period: '2024.04',
      customer: 'Sejong Government Complex',
      work: 'Hazard detection for cultural and sports facilities',
    },
    {
      period: '2023.07',
      customer: 'DL E&C / Lotte E&C',
      work: 'Construction site noise and hazard monitoring',
    },
    {
      period: '2023.05',
      customer: 'Incheon Startup Park',
      work: 'Hazard detection monitoring service',
    },
    {
      period: '2023.04',
      customer: 'Kangwon Land',
      work: 'Hazard monitoring for High1 Resort',
    },
    {
      period: '2022.07',
      customer: 'ROK Army Headquarters',
      work: 'Drone engine anomaly sound detection',
    },
    {
      period: '2022.05',
      customer: 'KB, ITP',
      work: 'Fraud prevention and theft detection sound project',
    },
    {
      period: '2022.03',
      customer: 'KT',
      work: 'Elderly care solution, IoT communication project',
    },
    {
      period: '2021.10',
      customer: 'LG CNS',
      work: 'Speaker emotion detection solution',
    },
    {
      period: '2021.10',
      customer: 'LG CNS',
      work: 'Defence technology collaboration',
    },
    {
      period: '2020.09',
      customer: 'Amazon',
      work: 'AWS disaster response collaboration',
    },
    {
      period: '2019.06',
      customer: 'Google',
      work: 'Google for Startups joint solution development',
    },
    {
      period: '2019.05',
      customer: 'LG CNS',
      work: 'AI sound development project',
    },
    {
      period: '2018.09',
      customer: 'Meta',
      work: 'Asia Startup Program (with Asia Foundation)',
    },
  ],
};
